import React, { useEffect, useRef } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StatusBadge from "./StatusBadge";
import { formatDateTime, fosDisplayLabel, fosHasChip } from "./partnersMobileUtils";
import css from "./partnersMobile.module.css";

// Labels and value styling mirror ViewFOSIDDetails so the page reads the same
// as the desktop modal.
function Row({ label, value, accent }) {
  return (
    <div className={css.kv}>
      <span className={css.kvKey}>{label}</span>
      <span
        className={css.kvValue}
        style={{ whiteSpace: "normal", color: accent ? "#1570ef" : undefined }}
      >
        {value || "NA"}
      </span>
    </div>
  );
}

// Intentionally no record-to-record pager: opening a FOS shows that record
// only, and the user returns to the list to pick another.
export default function FosDetail({ row, docs, onBack, onOverflow }) {
  const safeDocs = docs ?? {};
  const bodyRef = useRef(null);

  // The body is its own scroll container, so window.scrollTo would do nothing.
  // Reset it whenever the record changes, per the prev/next requirement.
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [row?.fosId]);

  // ViewFOSIDDetails treats an empty Aadhaar holder name as NA.
  const aadharName =
    safeDocs.aadhar?.docHolderName && safeDocs.aadhar.docHolderName !== ""
      ? safeDocs.aadhar.docHolderName
      : "NA";

  return (
    <div className={`partnersMobileRoot ${css.detailRoot}`}>
      <div className={css.detailHeader}>
        <button type="button" className={css.kebab} aria-label="Back" onClick={onBack}>
          <ArrowBackIcon sx={{ fontSize: 20 }} />
        </button>
        <span className={css.detailHeaderTitle}>{row.name || "—"}</span>
        <button
          type="button"
          className={css.kebab}
          aria-label="More actions"
          onClick={onOverflow}
        >
          <MoreVertIcon sx={{ fontSize: 20 }} />
        </button>
      </div>

      <div className={css.detailBody} ref={bodyRef}>
        <div className={css.detailCard}>
          <div className={css.detailIdentity}>
            <span className={css.cardId}>{fosDisplayLabel(row)}</span>
            {fosHasChip(row) && (
              <StatusBadge status={row.approvalStatus} disabled={row.isDisabled} />
            )}
          </div>
        </div>

        {/* ViewFOSIDDetails' top block: name, email | phone. */}
        <div className={css.detailCard}>
          <div className={css.detailCardTitle}>Contact</div>
          <Row label="Name" value={row.name} />
          <Row label="Email" value={row.email} />
          <Row label="Phone" value={row.contactNumber} />
        </div>

        <div className={css.detailCard}>
          <div className={css.detailCardTitle}>General Details</div>
          {/* The parent channel partner — shown as "CP" on desktop. */}
          <Row label="CP" value={row.cpName} />
          <Row label="PAN Number" value={safeDocs.pan?.docNumber} />
          <Row label="PAN Name" value={safeDocs.pan?.docHolderName} accent />
          <Row label="Aadhar Number" value={safeDocs.aadhar?.docNumber} />
          <Row label="Aadhar Name" value={aadharName} accent />
        </div>

        <div className={css.detailCard}>
          <div className={css.detailCardTitle}>Bank Details</div>
          <Row label="Account Holder Name" value={row.bankAccountHolderName} />
          <Row label="Account Number" value={row.bankAccountNumber} />
          <Row label="IFSC Code" value={row.bankIFSCCode} />
          <Row label="Branch Name" value={row.bankBranchName} />
          <Row label="Created At" value={formatDateTime(row.createdAt)} />
          <Row label="Updated At" value={formatDateTime(row.lastUpdated)} />
        </div>
      </div>
    </div>
  );
}
